/**
 * Compare transport (#140): corpus.*, compare.* and todos.* commands through
 * the TYPO3 proxy (type=3002). Proxy mode only. The proxy owns persistence,
 * rights and the gateway calls; this class only sends and parses.
 */

import { buildProxyHeaders, isNotFound, type AuthService } from './auth-utils';
import { GatewayError, GatewayErrorCode } from './gateway/error-codes';
import {
  proxyPost,
  throwIfCoded,
  type ProxyRequestBuilder,
} from './gateway/proxy-post';

export type Facets = {
  languages: { code: string; label: string }[];
  countries: { code: string; label: string }[];
  nace: {
    code: string;
    label: string;
    divisions: { code: string; label: string }[];
  }[];
};

export type Selection = {
  language: string;
  countries: string[];
  nace: string[];
  documentIds: string[];
};

export type CompareJob = {
  jobId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  summary: { added: number; kept: number; dismissed: number } | null;
  error: string | null;
};

export type TodoState = 'open' | 'done' | 'dismissed';

export type Todo = {
  id: string;
  documentId: string;
  documentName: string;
  comparedAt: string;
  state: TodoState;
  dismissReason: 'auto' | 'user' | null;
  isNew: boolean;
  kind: 'contradiction' | 'overlap';
  passage: { text: string; blockId: string };
  counterpart: {
    type: 'regulation' | 'document';
    label: string;
    country: string | null;
    documentId: string | null;
    removed: boolean;
  };
};

export interface CompareServiceOptions {
  proxyUrl: string;
  authService: AuthService;
}

/** Proxy codes in a 400/409 body that imsight acts on. */
const CODED: Record<string, GatewayErrorCode> = {
  COMPARE_RUNNING: GatewayErrorCode.COMPARE_RUNNING,
  INVALID_INPUT: GatewayErrorCode.INVALID_INPUT,
};

export default class CompareService {
  constructor(private readonly options: CompareServiceOptions) {}

  async facets(): Promise<Facets> {
    return this.require<Facets>(await this.command('corpus.facets', {}));
  }

  async start(input: Selection & { documentId: string }): Promise<CompareJob> {
    return this.require<CompareJob>(await this.command('compare.start', input));
  }

  async status(jobId: string): Promise<CompareJob> {
    return this.require<CompareJob>(
      await this.command('compare.status', { jobId })
    );
  }

  async listTodos(filter: {
    documentId?: string;
    state?: TodoState;
  }): Promise<{ items: Todo[]; selection: Selection | null }> {
    const data = this.require<{
      items?: Todo[];
      selection?: Selection | null;
    }>(await this.command('todos.list', filter));
    return { items: data.items ?? [], selection: data.selection ?? null };
  }

  async updateTodo(todoId: string, state: TodoState): Promise<Todo> {
    const data = this.require<{ item?: Todo }>(
      await this.command('todos.update', { todoId, state })
    );
    if (!data.item) throw invalidResponse();
    return data.item;
  }

  private require<T>(data: unknown): T {
    if (data === undefined || data === null) throw invalidResponse();
    return data as T;
  }

  private async command(
    command: string,
    data: Record<string, unknown>
  ): Promise<unknown> {
    try {
      const response = await this.send((csrf) => ({
        headers: buildProxyHeaders(csrf),
        body: JSON.stringify({
          command,
          language: 'eng',
          data: JSON.stringify(data),
        }),
      }));
      return ((await response.json()) as { data?: unknown }).data;
    } catch (error) {
      if (isNotFound(error)) {
        throw new GatewayError('Not found', GatewayErrorCode.NOT_FOUND, {
          status: 404,
        });
      }
      throw error;
    }
  }

  private send(build: ProxyRequestBuilder): Promise<Response> {
    return proxyPost(this.options, build, {
      label: 'Compare',
      onFailure: async (response) => {
        if (response.status === 409 || response.status === 400) {
          await throwIfCoded(response, CODED, 'Compare');
        }
      },
    });
  }
}

function invalidResponse(): GatewayError {
  return new GatewayError(
    'Invalid compare response',
    GatewayErrorCode.INVALID_RESPONSE,
    { status: 200 }
  );
}
